import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";

export default function TermsOfService() {
  const [s, setS] = useState({ support_email: "support@genuine.software", site_name: "genuine.software" });
  useEffect(() => {
    api.get("/settings/public").then((r) => setS((p) => ({ ...p, ...r.data }))).catch(() => {});
  }, []);
  const updated = "May 1, 2026";
  const email = s.support_email || "support@genuine.software";

  return (
    <div data-testid="terms-of-service-page" className="max-w-4xl mx-auto px-6 py-16">
      <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// legal</div>
      <h1 className="font-heading font-bold text-4xl md:text-5xl mt-2 leading-tight">Terms of Service</h1>
      <p className="text-[#71717A] mt-3 font-mono text-xs">Last updated: {updated}</p>

      <div className="mt-10 space-y-8 text-[#D4D4D8] leading-relaxed text-[15px]">
        <Section title="1. Acceptance of these Terms">
          <p>
            These Terms of Service ("Terms") form a binding legal agreement between you ("you",
            "Customer") and genuine.software ("we", "us", "our"). By accessing the website,
            placing an order, or using any of our services you accept these Terms in full. If you
            do not accept them, please do not use the service.
          </p>
          <p>
            You must be at least 18 years old (or the age of legal majority in your jurisdiction)
            to make a purchase. By placing an order you represent that you meet this requirement
            and that the payment method you use is yours or you are authorised to use it.
          </p>
        </Section>

        <Section title="2. Description of services">
          <p>
            We are an independent retailer of original digital software licenses for products
            including but not limited to Microsoft Windows, Microsoft Office, antivirus
            subscriptions and Autodesk product family. We are not affiliated with, endorsed by, or
            sponsored by Microsoft, Autodesk, or any other manufacturer named on this website. All
            trademarks remain the property of their respective owners and are used solely to
            identify the product offered for sale.
          </p>
          <p>
            Licenses are delivered electronically — by email, on the order-success page, and via
            the order-lookup tool — typically within 60 seconds of payment confirmation. Some
            account-based subscriptions (e.g. Autodesk) may take up to 24 hours of business time
            to be activated against the account email you supplied.
          </p>
        </Section>

        <Section title="3. Pricing, taxes and payment">
          <ul className="list-disc pl-6 space-y-2">
            <li>All prices are quoted in USD unless stated otherwise and are inclusive of any applicable platform fees.</li>
            <li>Customers are responsible for any local taxes, VAT, GST, customs duties or banking charges that may apply in their jurisdiction.</li>
            <li>Payment is processed by <strong>Stripe, Inc.</strong> using TLS-encrypted, PCI-DSS Level 1 certified infrastructure. We never see, store, or transmit your full card number.</li>
            <li>By submitting payment you authorise us, through Stripe, to charge the amount displayed at checkout to your selected payment method.</li>
            <li>Orders are typically fulfilled automatically once Stripe confirms a successful charge. Suspected-fraud orders may be held for manual review for up to 24 hours.</li>
          </ul>
        </Section>

        <Section title="4. Refund and cancellation policy">
          <p>
            Because the goods we sell are <strong>digital products that are delivered immediately
            and cannot be returned</strong>, refunds are subject to the following conditions:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Before delivery:</strong> if you contact us before any license key has been issued or any account-based subscription has been activated, you are entitled to a full refund within 24 hours.</li>
            <li><strong>Defective key:</strong> if a key fails to activate due to a fault on our side and we cannot supply a working replacement within 7 days, you are entitled to a full refund.</li>
            <li><strong>After successful activation:</strong> because the license has already been consumed and cannot be re-sold, refunds are not available for activated keys, except where required by mandatory consumer-protection law in your jurisdiction.</li>
            <li><strong>Duplicate or accidental orders:</strong> refunded in full if reported within 24 hours and the keys are unused.</li>
            <li>All approved refunds are returned to the original payment method via Stripe within 5–10 business days. We do not issue store credit unless explicitly requested.</li>
          </ul>
          <p>
            To request a refund, email <a className="text-[#00FF66] hover:underline" href={`mailto:${email}`}>{email}</a> with your order ID and a description of the issue. We respond within one business day.
          </p>
        </Section>

        <Section title="5. License terms with the manufacturer">
          <p>
            The license you purchase from us is governed by the End User License Agreement (EULA)
            of the original software publisher. You agree to read and abide by that EULA. We
            supply only the activation credential — the relationship between you and the
            publisher (Microsoft, Autodesk, the antivirus vendor, etc.) is governed by their
            terms.
          </p>
          <p>
            Each license is sold for use on the number of devices, users, or seats specified in
            the product description. Resale of activated keys is prohibited.
          </p>
        </Section>

        <Section title="6. Acceptable use and prohibited conduct">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>use the service for any unlawful, fraudulent, or deceptive purpose;</li>
            <li>use stolen payment methods or impersonate another person;</li>
            <li>resell, sublicense, distribute, or transfer activated license keys;</li>
            <li>attempt to bypass, probe, or disrupt the security of the website (rate limiting, captcha, authentication, etc.);</li>
            <li>use any automated tool, bot, or scraper to access the service without prior written consent;</li>
            <li>upload malware, exploit content, or content that infringes third-party rights;</li>
            <li>file an unjustified chargeback in lieu of contacting our support team.</li>
          </ul>
          <p>We reserve the right to refuse, cancel, or revoke any order that we reasonably believe violates these Terms or applicable law.</p>
        </Section>

        <Section title="7. Intellectual property">
          <p>
            The website, its design, layout, code, graphics, logos, and original copy are the
            property of genuine.software or its licensors and are protected by copyright,
            trademark, and other intellectual-property laws. You may not copy, modify, distribute,
            or create derivative works without our prior written consent. Software product names
            and logos belong to their respective owners.
          </p>
        </Section>

        <Section title="8. Disclaimers">
          <p>
            The service is provided on an "as is" and "as available" basis. We make commercially
            reasonable efforts to keep the website online, secure, and accurate, but we do not
            warrant that it will be uninterrupted, error-free, or free from defects. To the
            maximum extent permitted by law, we disclaim all warranties — express or implied —
            including merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the fullest extent permitted by law, our aggregate liability arising from or
            related to your use of the service or any product purchased from us shall not exceed
            the amount you paid us for the order giving rise to the claim. We shall not be liable
            for any indirect, incidental, special, consequential, exemplary, or punitive damages
            (including lost profits, lost data, or business interruption) even if advised of the
            possibility of such damages. Nothing in these Terms excludes or limits liability that
            cannot lawfully be excluded — for example liability for fraud or for death or personal
            injury caused by negligence.
          </p>
        </Section>

        <Section title="10. Indemnification">
          <p>
            You agree to defend, indemnify and hold harmless genuine.software, its operators and
            partners from and against any and all claims, damages, costs, and expenses (including
            reasonable lawyers&apos; fees) arising from your breach of these Terms, your misuse of
            the service, or your violation of any third-party right or law.
          </p>
        </Section>

        <Section title="11. Chargebacks">
          <p>
            If you believe a charge is incorrect or that you have not received what you paid for,
            please contact <a className="text-[#00FF66] hover:underline" href={`mailto:${email}`}>{email}</a> first
            so we can resolve the issue quickly. Filing a chargeback for a delivered, activated
            key without first attempting to resolve the matter with us may, at our discretion,
            result in suspension of future orders and pursuit of recovery of the disputed amount
            plus any chargeback fees imposed by Stripe.
          </p>
        </Section>

        <Section title="12. Privacy">
          <p>
            Your use of the service is also governed by our{" "}
            <Link className="text-[#00FF66] hover:underline" to="/privacy-policy">Privacy Policy</Link>,
            which is incorporated into these Terms by reference.
          </p>
        </Section>

        <Section title="13. Modifications">
          <p>
            We may update these Terms at any time. Material changes will be highlighted on the
            homepage banner for at least 30 days. Continued use of the service after changes take
            effect constitutes acceptance.
          </p>
        </Section>

        <Section title="14. Termination">
          <p>
            We may suspend or terminate your access to the service at any time, with or without
            cause, and with or without notice. Sections that by their nature should survive
            termination (intellectual property, disclaimers, limitation of liability,
            indemnification, governing law) will survive.
          </p>
        </Section>

        <Section title="15. Governing law and dispute resolution">
          <p>
            These Terms are governed by the laws of the seller&apos;s country of registration,
            without regard to its conflict-of-law rules. Any dispute arising under or in
            connection with these Terms shall first be addressed by good-faith negotiation. If
            negotiation fails within 30 days, the dispute shall be resolved by binding arbitration
            or by the competent courts of the seller&apos;s jurisdiction. Mandatory consumer
            rights in your country of residence are not affected.
          </p>
        </Section>

        <Section title="16. Severability">
          <p>
            If any provision of these Terms is held to be invalid or unenforceable, the remaining
            provisions will remain in full force and effect, and the invalid provision will be
            replaced with a valid one that most closely matches the intent of the original.
          </p>
        </Section>

        <Section title="17. Contact">
          <p>
            For any questions, refund requests, support, or formal notices, write to{" "}
            <a className="text-[#00FF66] hover:underline" href={`mailto:${email}`}>{email}</a>. We
            reply to every message within one business day.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="surface p-6 md:p-7 border border-white/5">
      <h2 className="font-heading font-semibold text-xl text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
